/**
 * Detalle de una corrida de certificación SII (platform admin, tenant-scoped).
 *
 * Reúne todas las fases sobre un `CertificationRun`: receptor de prueba, CAFs de
 * certificación, casos + mapeo ítem→producto, generar/firmar, enviar, consultar,
 * descargar XML/PDF, registrar el resultado del SII y aplicar la resolución al
 * tenant. Uso interno.
 */
import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, Loader2, Upload, Trash2, Pencil, FileDown, FileText,
  Send, RefreshCw, Cog, CheckCircle2, AlertTriangle,
} from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { inputCls, labelCompactCls } from '@/lib/formStyles'
import { extractErrorMessage } from '@/lib/errorUtils'
import {
  useCertRun, useUpdateReceptor, useUploadCaf, useDeleteCaf, useUpdateCaseMapping,
  useGenerateRun, useSendRun, useQueryRun, useRegisterResult, useApplyResolution,
  useDeleteRun, downloadRunXml, downloadCasePdf,
  type CertCase, type CertItemProduct,
} from '@/modules/certification/hooks/useCertification'
import { StatusBadge } from './CertificationPage'
import { Modal } from '@/components/ds/Modal'

const RUN_STATUS_STYLES: Record<string, string> = {
  draft: 'bg-glass-3 text-content-muted',
  generated: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  sent: 'bg-warning-surface-strong text-warning-content',
  accepted: 'bg-success-surface-strong text-success-content',
  rejected: 'bg-danger-surface-strong text-danger-content',
  error: 'bg-danger-surface-strong text-danger-content',
}
const RESULT_STATUS_STYLES: Record<string, string> = {
  pending: 'bg-glass-3 text-content-muted',
  cumple: 'bg-success-surface-strong text-success-content',
  reparo: 'bg-warning-surface-strong text-warning-content',
  rechazado: 'bg-danger-surface-strong text-danger-content',
}
const CASE_STATUS_STYLES: Record<string, string> = {
  pending: 'bg-glass-3 text-content-muted',
  generated: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  accepted: 'bg-success-surface-strong text-success-content',
  reparo: 'bg-warning-surface-strong text-warning-content',
  rejected: 'bg-danger-surface-strong text-danger-content',
}

type Feedback = { type: 'error' | 'success'; msg: string } | null

function Card({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-5 shadow-card">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-content">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  )
}

// ── Mapeo ítem→producto de un caso ────────────────────────────────────────────

function CaseMappingModal({ runId, kase, onClose, onError, onSaved }: {
  runId: string; kase: CertCase; onClose: () => void
  onError: (m: string) => void; onSaved: () => void
}) {
  const [map, setMap] = useState<Record<string, CertItemProduct>>(kase.item_products || {})
  const [docType, setDocType] = useState(kase.document_type || '')
  const update = useUpdateCaseMapping(runId)

  const setField = (idx: number, field: keyof CertItemProduct, value: string | boolean) => {
    setMap(m => ({ ...m, [idx]: { ...(m[String(idx)] || {}), [field]: value } }))
  }
  const setGlosa = (field: 'nombre' | 'descripcion', value: string) => {
    setMap(m => ({ ...m, _glosa: { ...(m._glosa || {}), [field]: value } }))
  }
  const glosa = map._glosa || {}
  const sinItems = (kase.items || []).length === 0

  const submit = () => {
    update.mutate(
      { caseId: kase.id, item_products: map, document_type: docType },
      { onSuccess: () => { onSaved(); onClose() }, onError: (e) => onError(extractErrorMessage(e, 'No se pudo guardar el mapeo.')) },
    )
  }

  return (
    <Modal open onClose={onClose} title={<>Caso {kase.case_number} — mapeo de ítems</>} subtitle={kase.document_type_label} size="2xl"
      actions={<>
        <button onClick={onClose} className="rounded-lg border border-divider px-3 py-1.5 text-sm text-content-muted hover:bg-soft">Cancelar</button>
        <button onClick={submit} disabled={update.isPending}
          className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm text-on-primary hover:bg-primary-hover disabled:opacity-60">
          {update.isPending && <Loader2 size={14} className="animate-spin" />} Guardar
        </button>
      </>}
    >
      <div className="max-h-[65vh] space-y-4 overflow-y-auto">
        <div>
          <label htmlFor="certificationrundetailpage-tipo-de-documento-codigo-interno" className={labelCompactCls}>Tipo de documento (código interno)</label>
          <input id="certificationrundetailpage-tipo-de-documento-codigo-interno" value={docType} onChange={e => setDocType(e.target.value.toUpperCase())}
            placeholder="FE / FNA / NC / ND" className={`${inputCls} w-40`} />
        </div>
        {sinItems && (
          <div className="rounded-lg border border-divider p-3">
            <p className="mb-2 text-xs text-content-muted">
              Documento sin ítems. Si <strong>corrige texto</strong> (giro, glosa), indica la
              corrección abajo: va como una línea de detalle con monto 0. Si <strong>anula</strong>,
              déjalo vacío: hereda los ítems del documento referenciado.
            </p>
            <div className="space-y-2">
              <div>
                <label htmlFor="certificationrundetailpage-glosa-nmbitem" className={labelCompactCls}>Glosa (NmbItem)</label>
                <input id="certificationrundetailpage-glosa-nmbitem" value={glosa.nombre ?? ''} onChange={e => setGlosa('nombre', e.target.value)}
                  className={inputCls} placeholder="Corrige giro del receptor" />
              </div>
              <div>
                <label htmlFor="certificationrundetailpage-detalle-donde-dice-debe-decir-dscitem" className={labelCompactCls}>Detalle — DONDE DICE … DEBE DECIR … (DscItem)</label>
                <textarea id="certificationrundetailpage-detalle-donde-dice-debe-decir-dscitem" value={glosa.descripcion ?? ''} onChange={e => setGlosa('descripcion', e.target.value)}
                  rows={2} className={inputCls}
                  placeholder="DONDE DICE SEGUROS DEBE DECIR COMPAÑIA ASEGURADORA" />
              </div>
            </div>
          </div>
        )}
        <div className="space-y-3">
          {(kase.items || []).map((it, i) => {
            const idx = i + 1
            const prod = map[String(idx)] || {}
            return (
              <div key={idx} className="rounded-lg border border-divider p-3">
                <p className="mb-2 text-xs text-content-muted">
                  ITEM {idx} · cantidad {it.cantidad ?? '—'} · precio set {it.precio_unitario ?? '—'}
                  {it.exento === true ? ' · exento' : it.exento === false ? ' · afecto' : ''}
                </p>
                <div className="grid gap-2 sm:grid-cols-3">
                  <div>
                    <label className={labelCompactCls}>Nombre producto</label>
                    <input aria-label="Nombre producto" value={prod.nombre ?? ''} onChange={e => setField(idx, 'nombre', e.target.value)}
                      className={inputCls} placeholder={`ITEM ${idx}`} />
                  </div>
                  <div>
                    <label className={labelCompactCls}>Precio (si el set no lo trae)</label>
                    <input aria-label="Precio (si el set no lo trae)" value={prod.precio_unitario ?? ''} onChange={e => setField(idx, 'precio_unitario', e.target.value)}
                      className={inputCls} placeholder={it.precio_unitario ?? '0'} />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-content">
                      <input type="checkbox" checked={!!prod.exento}
                        onChange={e => setField(idx, 'exento', e.target.checked)} />
                      Exento
                    </label>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </Modal>
  )
}

// ── Página ────────────────────────────────────────────────────────────────────

export function CertificationRunDetailPage() {
  const { runId = '' } = useParams<{ runId: string }>()
  const navigate = useNavigate()
  const { data: run, isLoading } = useCertRun(runId)
  const [fb, setFb] = useState<Feedback>(null)
  const [editCase, setEditCase] = useState<CertCase | null>(null)

  const updateReceptor = useUpdateReceptor(runId)
  const uploadCaf = useUploadCaf(runId)
  const deleteCaf = useDeleteCaf(runId)
  const generate = useGenerateRun(runId)
  const send = useSendRun(runId)
  const query = useQueryRun(runId)
  const registerResult = useRegisterResult(runId)
  const applyResolution = useApplyResolution(runId)
  const deleteRun = useDeleteRun()

  // Estado de forms locales
  const [receptor, setReceptor] = useState({ rut: '', name: '', giro: '', address: '', commune: '' })
  const [result, setResult] = useState({ status: 'cumple', notes: '' })
  const [resolution, setResolution] = useState({ number: '', date: '', activate: true, production: false })

  // Inicializa los forms UNA sola vez por run (guard por id): un refetch posterior
  // no debe pisar lo que el operador esté tipeando (ver [[document-modal-reset-race-refetch]]).
  const initedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!run || initedRef.current === run.id) return
    initedRef.current = run.id
    setReceptor({
      rut: run.receptor_rut, name: run.receptor_legal_name, giro: run.receptor_giro,
      address: run.receptor_address, commune: run.receptor_commune,
    })
    setResult({ status: run.result_status === 'pending' ? 'cumple' : run.result_status, notes: run.result_notes })
    setResolution(r => ({ ...r, number: run.resolution_number, date: run.resolution_date || '' }))
  }, [run])

  if (isLoading || !run) {
    return <div className="p-6"><Loader2 className="mx-auto animate-spin text-content-subtle" /></div>
  }

  const ok = (msg: string) => setFb({ type: 'success', msg })
  const err = (e: unknown, fallback: string) => setFb({ type: 'error', msg: extractErrorMessage(e, fallback) })

  const readCaf = (file: File) => {
    const reader = new FileReader()
    reader.onload = () => {
      uploadCaf.mutate(String(reader.result || ''), {
        onSuccess: () => ok('CAF cargado.'),
        onError: (e) => err(e, 'No se pudo cargar el CAF.'),
      })
    }
    reader.readAsText(file, 'ISO-8859-1')
  }

  const saveReceptor = () => {
    updateReceptor.mutate({
      receptor_rut: receptor.rut, receptor_legal_name: receptor.name, receptor_giro: receptor.giro,
      receptor_address: receptor.address, receptor_commune: receptor.commune,
    }, { onSuccess: () => ok('Receptor actualizado.'), onError: (e) => err(e, 'No se pudo guardar el receptor.') })
  }

  const doGenerate = () => generate.mutate(undefined, {
    onSuccess: () => ok('Envío generado y firmado.'), onError: (e) => err(e, 'No se pudo generar.'),
  })
  const doSend = () => send.mutate(undefined, {
    onSuccess: () => ok('Set enviado al SII.'), onError: (e) => err(e, 'No se pudo enviar.'),
  })
  const doQuery = () => query.mutate(undefined, {
    onSuccess: () => ok('Estado actualizado.'), onError: (e) => err(e, 'No se pudo consultar.'),
  })
  const doResult = () => registerResult.mutate({ result_status: result.status, result_notes: result.notes }, {
    onSuccess: () => ok('Resultado registrado.'), onError: (e) => err(e, 'No se pudo registrar el resultado.'),
  })
  const doResolution = () => applyResolution.mutate({
    resolution_number: resolution.number, resolution_date: resolution.date,
    activate_dte_types: resolution.activate, set_production: resolution.production,
  }, {
    onSuccess: () => ok('Resolución aplicada al tenant.'),
    onError: (e) => err(e, 'No se pudo aplicar la resolución.'),
  })
  const doDelete = () => {
    if (!window.confirm('¿Eliminar esta corrida de certificación? Se borran sus casos y CAFs.')) return
    deleteRun.mutate(runId, { onSuccess: () => navigate('/certification'), onError: (e) => err(e, 'No se pudo eliminar.') })
  }

  const busy = generate.isPending || send.isPending || query.isPending

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-4 sm:p-6">
      {/* Encabezado */}
      <div>
        <button onClick={() => navigate('/certification')}
          className="mb-3 inline-flex items-center gap-1 text-sm text-content-muted hover:text-content">
          <ArrowLeft size={15} /> Certificación
        </button>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-content">
              {run.set_type || 'Set'} · N° {run.numero_atencion}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <StatusBadge label={run.status_display} styleKey={run.status} styles={RUN_STATUS_STYLES} />
              <StatusBadge label={`Resultado: ${run.result_status_display}`} styleKey={run.result_status} styles={RESULT_STATUS_STYLES} />
              {run.track_id && <span className="text-xs text-content-subtle">Track ID: {run.track_id}</span>}
              <span className="rounded bg-soft px-2 py-0.5 text-xs text-content-subtle">Homologación</span>
            </div>
          </div>
          <button onClick={doDelete}
            className="inline-flex items-center gap-1 rounded-lg border border-divider px-3 py-1.5 text-sm text-danger-content-soft hover:bg-danger-surface">
            <Trash2 size={14} /> Eliminar
          </button>
        </div>
      </div>

      {fb && (
        <div className={`rounded-lg px-3 py-2 text-sm ${fb.type === 'error'
          ? 'bg-danger-surface text-danger-content'
          : 'bg-success-surface text-success-content'}`}>
          {fb.msg}
        </div>
      )}

      {/* Acciones principales */}
      <Card title="Acciones">
        <div className="flex flex-wrap gap-2">
          <button onClick={doGenerate} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm text-on-primary hover:bg-primary-hover disabled:opacity-60">
            {generate.isPending ? <Loader2 size={14} className="animate-spin" /> : <Cog size={14} />} Generar y firmar
          </button>
          <button onClick={doSend} disabled={busy || !run.has_signed_xml}
            className="inline-flex items-center gap-1.5 rounded-lg border border-divider px-3 py-1.5 text-sm text-content hover:bg-soft disabled:opacity-40">
            {send.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Enviar al SII
          </button>
          <button onClick={doQuery} disabled={busy || !run.track_id}
            className="inline-flex items-center gap-1.5 rounded-lg border border-divider px-3 py-1.5 text-sm text-content hover:bg-soft disabled:opacity-40">
            {query.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Consultar estado
          </button>
          <button onClick={() => downloadRunXml(runId, run.numero_atencion)} disabled={!run.has_signed_xml}
            className="inline-flex items-center gap-1.5 rounded-lg border border-divider px-3 py-1.5 text-sm text-content hover:bg-soft disabled:opacity-40">
            <FileDown size={14} /> Descargar XML del set
          </button>
        </div>
        {run.instructions && (
          <p className="mt-3 text-xs text-content-subtle">
            <strong>Instrucciones del set:</strong> {run.instructions}
          </p>
        )}
      </Card>

      {/* Receptor de prueba */}
      <Card title="Receptor de prueba">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="certificationrundetailpage-rut-receptor" className={labelCompactCls}>RUT receptor</label>
            <input id="certificationrundetailpage-rut-receptor" value={receptor.rut} onChange={e => setReceptor(r => ({ ...r, rut: e.target.value }))}
              placeholder="66666666-6" className={inputCls} />
          </div>
          <div>
            <label htmlFor="certificationrundetailpage-razon-social" className={labelCompactCls}>Razón social</label>
            <input id="certificationrundetailpage-razon-social" value={receptor.name} onChange={e => setReceptor(r => ({ ...r, name: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label htmlFor="certificationrundetailpage-giro" className={labelCompactCls}>Giro</label>
            <input id="certificationrundetailpage-giro" value={receptor.giro} onChange={e => setReceptor(r => ({ ...r, giro: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label htmlFor="certificationrundetailpage-comuna" className={labelCompactCls}>Comuna</label>
            <input id="certificationrundetailpage-comuna" value={receptor.commune} onChange={e => setReceptor(r => ({ ...r, commune: e.target.value }))} className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="certificationrundetailpage-direccion" className={labelCompactCls}>Dirección</label>
            <input id="certificationrundetailpage-direccion" value={receptor.address} onChange={e => setReceptor(r => ({ ...r, address: e.target.value }))} className={inputCls} />
          </div>
        </div>
        <div className="mt-3">
          <button onClick={saveReceptor} disabled={updateReceptor.isPending}
            className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm text-on-primary hover:bg-primary-hover disabled:opacity-60">
            {updateReceptor.isPending && <Loader2 size={14} className="animate-spin" />} Guardar receptor
          </button>
        </div>
      </Card>

      {/* CAFs de certificación */}
      <Card title="CAF de certificación" action={
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-divider px-3 py-1.5 text-sm text-content-muted hover:bg-soft">
          {uploadCaf.isPending ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Cargar CAF
          <input type="file" accept=".xml,text/xml,application/xml" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) readCaf(f); e.target.value = '' }} />
        </label>
      }>
        {run.cafs.length === 0 ? (
          <p className="text-sm text-content-subtle">Sin CAF cargados. El tipo y el rango se derivan del archivo.</p>
        ) : (
          <DataTable
            columns={[
              { key: 'document_type', header: 'Tipo', render: caf => <span className="font-medium text-content">{caf.document_type}</span> },
              { key: 'rango', header: 'Rango', render: caf => `${caf.folio_desde}–${caf.folio_hasta}` },
              { key: 'next_folio', header: 'Próximo folio' },
              {
                key: 'acciones', header: '',
                render: caf => (
                  <div className="text-right">
                    <button
                      onClick={() => deleteCaf.mutate(caf.id, { onError: (e) => err(e, 'No se pudo eliminar el CAF.') })}
                      className="rounded p-1 text-danger-solid hover:bg-danger-surface"
                      title="Eliminar"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ),
              },
            ]}
            rows={run.cafs}
            rowKey={caf => caf.id}
            minWidth={480}
            caption="CAF cargados para la certificación"
          />
        )}
      </Card>

      {/* Casos */}
      <Card title={`Casos (${run.cases.length})`}>
        <DataTable
          columns={[
            { key: 'case_number', header: 'Caso', render: c => <span className="font-medium text-content">{c.case_number}</span> },
            { key: 'document_type_label', header: 'Documento', render: c => c.document_type_label || '—' },
            {
              key: 'document_type', header: 'Tipo',
              render: c => c.document_type
                ? <span className="text-content">{c.document_type}</span>
                : <span className="inline-flex items-center gap-1 text-xs text-warning-content-soft"><AlertTriangle size={12} /> sin mapear</span>,
            },
            { key: 'folio', header: 'Folio', render: c => c.folio ?? '—' },
            {
              key: 'case_status', header: 'Estado',
              render: c => <StatusBadge label={c.case_status_display} styleKey={c.case_status} styles={CASE_STATUS_STYLES} />,
            },
            {
              key: 'acciones', header: '',
              render: c => (
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => setEditCase(c)} className="rounded p-1 text-content-subtle hover:bg-soft" title="Editar mapeo">
                    <Pencil size={14} />
                  </button>
                  {run.has_signed_xml && c.folio && (
                    <button
                      onClick={() => downloadCasePdf(runId, c.id, c.case_number).catch(() => err(null, 'No se pudo abrir el PDF.'))}
                      className="rounded p-1 text-content-subtle hover:bg-soft"
                      title="Descargar PDF"
                    >
                      <FileText size={14} />
                    </button>
                  )}
                </div>
              ),
            },
          ]}
          rows={run.cases}
          rowKey={c => c.id}
          minWidth={720}
          caption="Casos del set de certificación"
        />
      </Card>

      {/* Resultado del SII (Fase 4) */}
      <Card title="Resultado del SII (registro manual)">
        <p className="mb-3 text-xs text-content-muted">
          Registra el veredicto administrativo que el SII envía por correo al buzón del RUT. El estado técnico
          de recepción del envío lo lleva el track ID.
        </p>
        <div className="grid gap-3 sm:grid-cols-[200px_1fr]">
          <div>
            <label htmlFor="certificationrundetailpage-resultado" className={labelCompactCls}>Resultado</label>
            <select id="certificationrundetailpage-resultado" value={result.status} onChange={e => setResult(r => ({ ...r, status: e.target.value }))} className={inputCls}>
              <option value="cumple">Cumple</option>
              <option value="reparo">Con reparos</option>
              <option value="rechazado">Rechazado</option>
            </select>
          </div>
          <div>
            <label htmlFor="certificationrundetailpage-notas" className={labelCompactCls}>Notas</label>
            <input id="certificationrundetailpage-notas" value={result.notes} onChange={e => setResult(r => ({ ...r, notes: e.target.value }))} className={inputCls} />
          </div>
        </div>
        <div className="mt-3">
          <button onClick={doResult} disabled={registerResult.isPending}
            className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm text-on-primary hover:bg-primary-hover disabled:opacity-60">
            {registerResult.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Registrar resultado
          </button>
        </div>
      </Card>

      {/* Resolución (Fase 5) */}
      <Card title="Resolución del SII → aplicar al tenant">
        <p className="mb-3 text-xs text-content-muted">
          Cuando el SII autoriza al emisor, registra el número y la fecha de resolución. Se copian al
          LocalizationConfig y se activan en <strong>/config/dte-types</strong> los tipos certificados en esta corrida.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="certificationrundetailpage-numero-de-resolucion" className={labelCompactCls}>Número de resolución</label>
            <input id="certificationrundetailpage-numero-de-resolucion" value={resolution.number} onChange={e => setResolution(r => ({ ...r, number: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label htmlFor="certificationrundetailpage-fecha-de-resolucion" className={labelCompactCls}>Fecha de resolución</label>
            <input id="certificationrundetailpage-fecha-de-resolucion" type="date" value={resolution.date} onChange={e => setResolution(r => ({ ...r, date: e.target.value }))} className={inputCls} />
          </div>
        </div>
        <div className="mt-3 space-y-2">
          <label className="flex items-center gap-2 text-sm text-content">
            <input type="checkbox" checked={resolution.activate} onChange={e => setResolution(r => ({ ...r, activate: e.target.checked }))} />
            Activar los tipos de documento de esta corrida en /config/dte-types
          </label>
          <label className="flex items-center gap-2 text-sm text-content">
            <input type="checkbox" checked={resolution.production} onChange={e => setResolution(r => ({ ...r, production: e.target.checked }))} />
            <span className="inline-flex items-center gap-1">
              Cambiar el ambiente del tenant a <strong>producción</strong>
              <AlertTriangle size={13} className="text-warning-solid" />
            </span>
          </label>
          {resolution.production && (
            <div className="rounded-lg bg-warning-surface px-3 py-2 text-xs text-warning-content">
              Alto impacto: el tenant empezará a emitir DTEs reales al SII. Confirma que la certificación está aprobada.
            </div>
          )}
        </div>
        <div className="mt-3">
          <button onClick={doResolution} disabled={applyResolution.isPending || !resolution.number || !resolution.date}
            className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm text-on-primary hover:bg-primary-hover disabled:opacity-60">
            {applyResolution.isPending && <Loader2 size={14} className="animate-spin" />} Aplicar resolución
          </button>
        </div>
      </Card>

      {editCase && (
        <CaseMappingModal runId={runId} kase={editCase} onClose={() => setEditCase(null)}
          onError={(m) => setFb({ type: 'error', msg: m })} onSaved={() => ok('Mapeo guardado.')} />
      )}
    </div>
  )
}
